import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as api from "./adminApi";
import {
  AdminShell,
  Btn,
  Card,
  Field,
  InlineForm,
  Row,
  Sel,
  StatusBadge,
  Text,
  useAsync,
} from "./components";

export function TrackPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: t, error, reload } = useAsync(() => api.getTrack(id), [id]);
  const { data: siblingTracks } = useAsync(
    () => api.listTracks(t?.programme.id),
    [t?.programme.id],
  );
  const { data: assessmentConfig, reload: reloadConfig } = useAsync(
    () => (t ? api.getAssessmentConfig(t.id) : Promise.resolve(null)),
    [t?.id],
  );
  const { data: diagnosticConfig, reload: reloadDiagnosticConfig } = useAsync(
    () => (t ? api.getDiagnosticConfig(t.id) : Promise.resolve(null)),
    [t?.id],
  );

  const [title, setTitle] = useState("");
  const [threshold, setThreshold] = useState(70);
  const [moduleTitle, setModuleTitle] = useState("");
  const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null);
  const [moduleError, setModuleError] = useState<string | null>(null);
  const [prereqIds, setPrereqIds] = useState<string[]>([]);
  const [creatingAssessment, setCreatingAssessment] = useState(false);
  const [passPercent, setPassPercent] = useState(70);
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [selectionMode, setSelectionMode] = useState("RANDOM_SAMPLE");
  const [questionsPresented, setQuestionsPresented] = useState(20);
  const [randomiseOrder, setRandomiseOrder] = useState(true);
  const [onFinalFailure, setOnFinalFailure] = useState("REMEDIATION");
  const [creatingDiagnostic, setCreatingDiagnostic] = useState(false);
  const [diagnosticQuestionCount, setDiagnosticQuestionCount] = useState(4);
  const [requirePre, setRequirePre] = useState(true);
  const [requirePost, setRequirePost] = useState(true);

  useEffect(() => {
    if (t) {
      setTitle(t.title);
      setThreshold(t.passThresholdPercent);
      setPrereqIds(t.prerequisites.map((p) => p.requiredTrackId));
    }
  }, [t]);

  useEffect(() => {
    if (assessmentConfig) {
      setPassPercent(assessmentConfig.passPercent);
      setMaxAttempts(assessmentConfig.maxAttempts);
      setSelectionMode(assessmentConfig.selectionMode);
      setQuestionsPresented(assessmentConfig.questionsPresented);
      setRandomiseOrder(assessmentConfig.randomiseOrder);
      setOnFinalFailure(assessmentConfig.onFinalFailure);
    }
  }, [assessmentConfig]);

  useEffect(() => {
    if (diagnosticConfig) {
      setDiagnosticQuestionCount(diagnosticConfig.questionCount);
      setRequirePre(diagnosticConfig.requirePre);
      setRequirePost(diagnosticConfig.requirePost);
    }
  }, [diagnosticConfig]);

  if (error)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Track">
        {error}
      </AdminShell>
    );
  if (!t)
    return (
      <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Loading…">
        {null}
      </AdminShell>
    );

  const others = (siblingTracks ?? []).filter((s) => s.id !== t.id);

  async function removeModule(moduleId: string, title: string) {
    if (
      !window.confirm(
        `Delete module "${title}"? This also deletes its learning items. This cannot be undone.`,
      )
    )
      return;
    setDeletingModuleId(moduleId);
    setModuleError(null);
    try {
      await api.deleteModule(moduleId);
      reload();
    } catch (e) {
      setModuleError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingModuleId(null);
    }
  }

  return (
    <AdminShell
      crumbs={[
        { label: "Admin", to: "/admin" },
        { label: t.programme.title, to: `/admin/programmes/${t.programme.id}` },
        { label: t.title },
      ]}
      title={t.title}
    >
      <Card>
        <div
          style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginBottom: "0.8rem" }}
        >
          <StatusBadge status={t.status} />
          <Btn
            kind="ghost"
            onClick={async () => {
              await api.setPublished("track", t.id, t.status !== "PUBLISHED");
              reload();
            }}
          >
            {t.status === "PUBLISHED" ? "Unpublish" : "Publish"}
          </Btn>
          {t.programme.status !== "PUBLISHED" && (
            <span style={{ color: "#b00020", fontSize: "0.8rem" }}>
              Programme must be published first
            </span>
          )}
        </div>
        <InlineForm
          submitLabel="Save"
          onSubmit={async () => {
            await api.updateTrack(t.id, { title, passThresholdPercent: threshold });
            reload();
          }}
        >
          <Field label="Title">
            <Text value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <Field label="Pass threshold % (0 = no graded assessment)">
            <Text
              type="number"
              min={0}
              max={100}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
          </Field>
        </InlineForm>
      </Card>

      <Card>
        <strong>Prerequisites</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          Tracks the learner must pass before this one unlocks.
        </p>
        {others.length === 0 ? (
          <p style={{ color: "#888" }}>No other tracks in this programme.</p>
        ) : (
          <>
            {others.map((o) => (
              <label key={o.id} style={{ display: "block", padding: "0.2rem 0" }}>
                <input
                  type="checkbox"
                  checked={prereqIds.includes(o.id)}
                  onChange={(e) =>
                    setPrereqIds((prev) =>
                      e.target.checked ? [...prev, o.id] : prev.filter((x) => x !== o.id),
                    )
                  }
                />{" "}
                {o.title}
              </label>
            ))}
            <div style={{ marginTop: "0.5rem" }}>
              <InlineForm
                submitLabel="Save prerequisites"
                onSubmit={async () => {
                  await api.setPrerequisites(t.id, prereqIds);
                  reload();
                }}
              >
                {null}
              </InlineForm>
            </div>
          </>
        )}
      </Card>

      <Card>
        <strong>Modules</strong>
        {moduleError && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{moduleError}</p>}
        {t.modules.length === 0 && <p style={{ color: "#666" }}>No modules yet.</p>}
        {t.modules
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((m) => (
            <Row key={m.id}>
              <Link to={`/admin/modules/${m.id}`}>
                {m.order + 1}. {m.title}
                {!m.required && <span style={{ color: "#888" }}> (optional)</span>}
              </Link>
              <span style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <StatusBadge status={m.status} />
                <Btn
                  kind="danger"
                  onClick={() => removeModule(m.id, m.title)}
                  disabled={deletingModuleId === m.id}
                >
                  {deletingModuleId === m.id ? "Deleting…" : "Delete"}
                </Btn>
              </span>
            </Row>
          ))}
        <div style={{ marginTop: "0.8rem" }}>
          <InlineForm
            submitLabel="Add module"
            onSubmit={async () => {
              const { id: mid } = await api.createModule({
                trackId: t.id,
                title: moduleTitle,
                order: t.modules.length,
              });
              setModuleTitle("");
              nav(`/admin/modules/${mid}`);
            }}
          >
            <Field label="Module title">
              <Text value={moduleTitle} onChange={(e) => setModuleTitle(e.target.value)} required />
            </Field>
          </InlineForm>
        </div>
      </Card>

      <Card>
        <strong>Assessment</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          If this track has no assessment configured, the learner passes as soon as all mandatory
          modules are complete.
        </p>
        {!assessmentConfig ? (
          <Btn
            onClick={async () => {
              setCreatingAssessment(true);
              try {
                const bank = await api.createQuestionBank({
                  title: `${t.title} — Question Bank`,
                  trackId: t.id,
                });
                await api.saveAssessmentConfig(t.id, {
                  bankId: bank.id,
                  passPercent: 70,
                  maxAttempts: 2,
                  selectionMode: "RANDOM_SAMPLE",
                  questionsPresented: 20,
                  randomiseOrder: true,
                  onFinalFailure: "REMEDIATION",
                });
                reloadConfig();
              } finally {
                setCreatingAssessment(false);
              }
            }}
            disabled={creatingAssessment}
          >
            {creatingAssessment ? "Creating…" : "Add an assessment to this track"}
          </Btn>
        ) : (
          <>
            <p style={{ marginTop: "0.5rem" }}>
              <Link to={`/admin/question-banks/${assessmentConfig.bankId}`}>
                Manage questions →
              </Link>
            </p>
            <InlineForm
              submitLabel="Save assessment settings"
              onSubmit={async () => {
                await api.saveAssessmentConfig(t.id, {
                  bankId: assessmentConfig.bankId,
                  passPercent,
                  maxAttempts,
                  selectionMode,
                  questionsPresented,
                  randomiseOrder,
                  onFinalFailure,
                });
                reloadConfig();
              }}
            >
              <Field label="Pass percent">
                <Text
                  type="number"
                  min={1}
                  max={100}
                  value={passPercent}
                  onChange={(e) => setPassPercent(Number(e.target.value))}
                />
              </Field>
              <Field label="Max attempts">
                <Text
                  type="number"
                  min={1}
                  max={10}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(Number(e.target.value))}
                />
              </Field>
              <Field label="Question selection">
                <Sel
                  value={selectionMode}
                  onChange={(e) => setSelectionMode(String(e.target.value))}
                >
                  <option value="RANDOM_SAMPLE">Random sample from the bank</option>
                  <option value="SHUFFLE_ALL">Use every question, shuffled</option>
                </Sel>
              </Field>
              {selectionMode === "RANDOM_SAMPLE" && (
                <Field label="Questions presented per attempt">
                  <Text
                    type="number"
                    min={1}
                    max={200}
                    value={questionsPresented}
                    onChange={(e) => setQuestionsPresented(Number(e.target.value))}
                  />
                </Field>
              )}
              <label style={{ display: "block", marginBottom: "0.7rem", fontSize: "0.9rem" }}>
                <input
                  type="checkbox"
                  checked={randomiseOrder}
                  onChange={(e) => setRandomiseOrder(e.target.checked)}
                />{" "}
                Randomise question order per attempt
              </label>
              <Field label="On final failed attempt">
                <Sel
                  value={onFinalFailure}
                  onChange={(e) => setOnFinalFailure(String(e.target.value))}
                >
                  <option value="REMEDIATION">Require review before retrying (default)</option>
                  <option value="IMMEDIATE_RETRY">Allow immediate retry</option>
                  <option value="ADMIN_RESET">Only an admin can re-open attempts</option>
                </Sel>
              </Field>
            </InlineForm>
          </>
        )}
      </Card>

      <Card>
        <strong>Pre/post programme score</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          A short diagnostic (same bank of questions, once before the learner starts and once before
          the final assessment) to measure learning impact — separate from the graded assessment
          above, never affects pass/fail or the certificate.
        </p>
        {!diagnosticConfig ? (
          <Btn
            onClick={async () => {
              setCreatingDiagnostic(true);
              try {
                const bank = await api.createQuestionBank({
                  title: `${t.title} — Diagnostic Bank`,
                  trackId: t.id,
                  purpose: "DIAGNOSTIC",
                });
                await api.saveDiagnosticConfig(t.id, {
                  bankId: bank.id,
                  questionCount: 4,
                  requirePre: true,
                  requirePost: true,
                });
                reloadDiagnosticConfig();
              } finally {
                setCreatingDiagnostic(false);
              }
            }}
            disabled={creatingDiagnostic}
          >
            {creatingDiagnostic ? "Creating…" : "Add a pre/post diagnostic to this track"}
          </Btn>
        ) : (
          <>
            <p style={{ marginTop: "0.5rem" }}>
              <Link to={`/admin/question-banks/${diagnosticConfig.bankId}`}>
                Manage diagnostic questions →
              </Link>
            </p>
            <InlineForm
              submitLabel="Save diagnostic settings"
              onSubmit={async () => {
                await api.saveDiagnosticConfig(t.id, {
                  bankId: diagnosticConfig.bankId,
                  questionCount: diagnosticQuestionCount,
                  requirePre,
                  requirePost,
                });
                reloadDiagnosticConfig();
              }}
            >
              <Field label="Questions per diagnostic">
                <Text
                  type="number"
                  min={1}
                  max={50}
                  value={diagnosticQuestionCount}
                  onChange={(e) => setDiagnosticQuestionCount(Number(e.target.value))}
                />
              </Field>
              <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.9rem" }}>
                <input
                  type="checkbox"
                  checked={requirePre}
                  onChange={(e) => setRequirePre(e.target.checked)}
                />{" "}
                Pre-diagnostic required before starting the programme (unchecked = optional, learner
                can skip)
              </label>
              <label style={{ display: "block", marginBottom: "0.7rem", fontSize: "0.9rem" }}>
                <input
                  type="checkbox"
                  checked={requirePost}
                  onChange={(e) => setRequirePost(e.target.checked)}
                />{" "}
                Post-diagnostic required before the final assessment (unchecked = optional, learner
                can skip)
              </label>
            </InlineForm>
          </>
        )}
      </Card>
    </AdminShell>
  );
}
